/**
 * Two behavioural additions for the vertical thumbnail gallery (desktop, paired
 * with the [id^='MainProduct-'] .product--thumbnail_slider layout in
 * section-main-product.css).
 *
 * Per AGENTS.md §8, behavioural changes always live outside Dawn's files;
 * media-gallery.js is not modified. This only hooks Dawn's existing API from
 * the outside (setActiveThumbnail / preventStickyHeader), leaving the original
 * file upgradeable.
 *
 * Two things:
 *
 * 1) Thumbnail scroll axis. Dawn's setActiveThumbnail scrolls the active
 *    thumbnail into view with scrollTo({ left }); once thumbnails are stacked
 *    vertically, offsetLeft is always 0, that line becomes a no-op, and
 *    clicking the 9th thumbnail does not scroll the list. This adds a vertical
 *    scroll after the original method.
 *
 * 2) Suppress the header reveal while the gallery is stuck. The header uses
 *    sticky_header_type "on-scroll-up" and slides back in when scrolling up;
 *    it sits one layer above the gallery and is 116px tall, covering roughly
 *    86px of the top of the stuck main image. Dawn ships a
 *    'preventHeaderReveal' event (sticky-header switches to hide() on receipt),
 *    but it is one-shot (self-clearing after 66ms), so it must be re-fired on
 *    every scroll for as long as the gallery is genuinely stuck.
 *    This matches CB2 / Rejuvenation behaviour: the header stays away while the
 *    main image is pinned, until the info column on the right returns to the
 *    top of the viewport.
 */
(() => {
  const Gallery = customElements.get('media-gallery');
  if (!Gallery) return;

  const desktop = window.matchMedia('(min-width: 750px)');

  // ── 1. Scroll vertically when stacked ──────────────────────────────────
  const original = Gallery.prototype.setActiveThumbnail;
  Gallery.prototype.setActiveThumbnail = function (thumbnail) {
    original.call(this, thumbnail);

    const slider = this.elements?.thumbnails?.slider;
    if (!slider || !thumbnail) return;
    // No vertical overflow = still a horizontal row; the original method already handled it
    if (slider.scrollHeight <= slider.clientHeight + 1) return;

    // Already in view, so leave it alone; otherwise every click produces a jump
    const item = thumbnail.getBoundingClientRect();
    const box = slider.getBoundingClientRect();
    if (item.top >= box.top - 2 && item.bottom <= box.bottom + 2) return;

    // behavior: 'instant' is deliberate — setActiveMedia queues a scroll of its
    // own right afterwards, which would interrupt an in-flight smooth animation
    // and leave the active thumbnail parked out of view.
    slider.scrollTo({ top: thumbnail.offsetTop, behavior: 'instant' });
  };

  // ── 2. Suppress the header reveal while stuck ──────────────────────────
  // One document-level listener rather than per-element ones: product-info
  // re-renders the entire gallery on variant change, so listeners bound to the
  // elements die along with the old ones.
  let cached = null;
  const currentGallery = () => {
    if (cached && cached.isConnected) return cached;
    cached = document.querySelector('media-gallery.product__column-sticky');
    return cached;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (!desktop.matches) return;
      const gallery = currentGallery();
      if (!gallery || typeof gallery.preventStickyHeader !== 'function') return;

      const wrapper = gallery.closest('.product__media-wrapper');
      if (!wrapper) return;

      // Stuck test: while unstuck, the gallery and its wrapper share the same
      // top; once stuck the wrapper keeps scrolling up while the gallery holds
      // at the sticky offset, so the wrapper's top becomes the smaller value.
      if (wrapper.getBoundingClientRect().top < gallery.getBoundingClientRect().top) {
        gallery.preventStickyHeader();
      }
    },
    // Capture phase: the flag has to be set before StickyHeader's own scroll handler decides reveal / hide
    { capture: true, passive: true },
  );
})();
